import { useCallback, useRef, useSyncExternalStore } from 'react';

import { featureFlagClient } from './core/featureFlagClient';
import type { FeatureFlagProvider } from './core/featureFlagClient.types';
import type { FeatureFlagKey, PortalFeatureFlagDefinition } from './featureFlagDefinition';
import {
  type BooleanFeatureFlagResolution,
  getFeatureFlagBooleanDetails,
  resolveBooleanFeatureFlag,
} from './getFeatureFlagBooleanDetails';

type BooleanPortalFeatureFlagDefinition = PortalFeatureFlagDefinition<
  FeatureFlagKey,
  FeatureFlagProvider,
  'boolean'
>;

const PENDING_RESOLUTION: BooleanFeatureFlagResolution = { status: 'pending' };

function areResolutionsEqual(
  left: BooleanFeatureFlagResolution,
  right: BooleanFeatureFlagResolution
): boolean {
  if (left.status !== right.status) {
    return false;
  }
  if (left.status === 'pending' || right.status === 'pending') {
    return true;
  }
  return left.value === right.value;
}

/**
 * Waits until the provider is ready. Missing flags (`FLAG_NOT_FOUND`) resolve to
 * `true`. Other evaluation errors stay `pending`.
 */
export function useBooleanFeatureFlagResolution(
  definition: BooleanPortalFeatureFlagDefinition,
  defaultValue = false
): BooleanFeatureFlagResolution {
  const cachedSnapshotRef = useRef<BooleanFeatureFlagResolution>(PENDING_RESOLUTION);

  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      featureFlagClient.onChange(onStoreChange, { provider: definition.provider }),
    [definition.provider]
  );
  const getSnapshot = useCallback((): BooleanFeatureFlagResolution => {
    const details = getFeatureFlagBooleanDetails(definition, defaultValue);
    const isReady = featureFlagClient.isReady({ provider: definition.provider });
    const next = resolveBooleanFeatureFlag(details, isReady);
    const previous = cachedSnapshotRef.current;
    if (areResolutionsEqual(previous, next)) {
      return previous;
    }
    cachedSnapshotRef.current = next.status === 'pending' ? PENDING_RESOLUTION : next;
    return cachedSnapshotRef.current;
  }, [defaultValue, definition]);
  const getServerSnapshot = useCallback(() => PENDING_RESOLUTION, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
